import { type FormEvent } from 'react'
import { type InertiaFormProps } from '@inertiajs/react'
import { Calendar } from 'lucide-react'
import ImageUpload from '@/components/ImageUpload'
import RichTextEditor from '@/components/RichTextEditor'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { LinkableEvent } from '@/types'

export type PostFormData = {
  body: string
  image: string | null
  remove_image: boolean
  image_preview_url: string | null
  linked_event_id: string | null
  pinned: boolean
}

type Props = {
  form: InertiaFormProps<PostFormData>
  linkableEvents: LinkableEvent[]
  currentlyPinnedId: string | null
  submitLabel: string
  onSubmit: (e: FormEvent) => void
  errors?: Partial<Record<keyof PostFormData | 'image' | 'linked_event_id', string[]>>
  showPinControl?: boolean
}

function formatEventLabel(event: LinkableEvent): string {
  if (!event.date) return event.title
  // YYYY-MM-DD parses as UTC midnight via `new Date`; force a noon-local
  // parse so the wall-clock day matches what's stored (no off-by-one west of UTC).
  const date = new Date(`${event.date}T12:00:00`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
  return `${event.title} · ${date}`
}

export default function PostForm({
  form,
  linkableEvents,
  currentlyPinnedId,
  submitLabel,
  onSubmit,
  errors,
  showPinControl = true,
}: Props) {
  const { data, setData, processing } = form
  const showUnpinWarning = showPinControl && data.pinned && !!currentlyPinnedId

  const NONE_SENTINEL = '__none__'

  return (
    <form onSubmit={onSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label htmlFor="post-body">
          Post<span className="text-destructive"> *</span>
        </Label>
        <RichTextEditor
          content={data.body}
          onChange={html => setData('body', html)}
        />
        {errors?.body && (
          <p className="text-xs text-destructive">{errors.body.join(', ')}</p>
        )}
      </div>

      <div className="space-y-2">
        <Label>Image</Label>
        <ImageUpload
          currentUrl={data.image_preview_url}
          onUpload={(signedId) => {
            setData(d => ({
              ...d,
              image: signedId || null,
              remove_image: !signedId,
            }))
          }}
          onPreviewChange={(url) => setData('image_preview_url', url)}
        />
        {errors?.image && (
          <p className="text-xs text-destructive">{errors.image.join(', ')}</p>
        )}
      </div>

      {linkableEvents.length > 0 && (
        <div className="space-y-2">
          <Label htmlFor="post-linked-event">Link an event or fundraiser</Label>
          <Select
            value={data.linked_event_id ?? NONE_SENTINEL}
            onValueChange={(v) => setData('linked_event_id', v === NONE_SENTINEL ? null : v)}
          >
            <SelectTrigger id="post-linked-event">
              <Calendar className="size-4 text-muted-foreground" />
              <SelectValue placeholder="No linked event" />
            </SelectTrigger>
            <SelectContent>
              <SelectGroup>
                <SelectItem value={NONE_SENTINEL}>No linked event</SelectItem>
                {linkableEvents.map(event => (
                  <SelectItem key={event.id} value={event.id}>
                    {formatEventLabel(event)}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>
          {errors?.linked_event_id && (
            <p className="text-xs text-destructive">{errors.linked_event_id.join(', ')}</p>
          )}
        </div>
      )}

      {showPinControl && (
        <div className="flex items-start gap-3 rounded-lg border border-black/5 bg-muted/30 p-3">
          <Checkbox
            id="post-pinned"
            checked={data.pinned}
            onCheckedChange={v => setData('pinned', !!v)}
          />
          <div className="space-y-1">
            <Label htmlFor="post-pinned" className="text-sm font-medium">
              Pin to top of profile
            </Label>
            {showUnpinWarning && (
              <p className="text-xs text-muted-foreground">
                This will unpin your current pinned post.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="flex justify-end pt-2">
        <Button type="submit" disabled={processing}>
          {submitLabel}
        </Button>
      </div>
    </form>
  )
}
